import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, CircularProgress, MenuItem,
  Select, Typography
} from "@mui/material";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid,
  Cell, ResponsiveContainer, Tooltip, XAxis, YAxis
} from "recharts";
import {
  getDashboardCampaigns          as getCampaigns,
  getDashboardComplianceSummary  as getSummary,
  getDashboardComplianceByRule   as getByRule,
  getDashboardComplianceMtd      as getMtd,
} from "../../services/pbi/pbiservices";

// ── Constants ──────────────────────────────────────────────────────
const BLUE   = "#378ADD";
const PINK   = "#D4537E";
const AMBER  = "#BA7517";
const GREEN  = "#1D9E75";

const GRAN_OPTIONS = ["Day", "Week", "Month"];

// ── Date range presets ─────────────────────────────────────────────
const RANGE_OPTIONS = [
  "7D", "30D", "90D", "1Y",
  "C Wk", "CW+Last",
  "This Mo", "Cur+Last", "Prev 2Mo", "Prev 3Mo",
];

function getDateRange(preset) {
  const now = new Date();
  const y   = now.getFullYear();
  const m   = now.getMonth();
  let start, end;

  const dow        = now.getDay();
  const thisMonday = new Date(now);
  thisMonday.setDate(now.getDate() - ((dow + 6) % 7));
  thisMonday.setHours(0, 0, 0, 0);

  const lastMonday = new Date(thisMonday);
  lastMonday.setDate(thisMonday.getDate() - 7);

  switch (preset) {
    case "7D":
      start = new Date(now); start.setDate(now.getDate() - 7);   end = now; break;
    case "30D":
      start = new Date(now); start.setDate(now.getDate() - 30);  end = now; break;
    case "90D":
      start = new Date(now); start.setDate(now.getDate() - 90);  end = now; break;
    case "1Y":
      start = new Date(now); start.setDate(now.getDate() - 365); end = now; break;
    case "C Wk":
      start = thisMonday; end = now; break;
    case "CW+Last":
      start = lastMonday; end = now; break;
    case "This Mo":
      start = new Date(y, m, 1);     end = now; break;
    case "Cur+Last":
      start = new Date(y, m - 1, 1); end = now; break;
    case "Prev 2Mo":
      start = new Date(y, m - 2, 1); end = new Date(y, m, 0); break;
    case "Prev 3Mo":
    default:
      start = new Date(y, m - 3, 1); end = new Date(y, m, 0); break;
  }

  const toLocal = (d) => {
    const y  = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    const dy = String(d.getDate()).padStart(2, "0");
    return `${y}-${mo}-${dy}`;
  };
  return {
    startDate: toLocal(start),
    endDate:   toLocal(end),
  };
}

// Previous period = same duration as current, ending the day before current starts.
function getPrevDateRange(startDate, endDate) {
  const parse = (s) => {
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(s);
  };
  const start  = parse(startDate);
  const end    = parse(endDate);
  const durMs  = end.getTime() - start.getTime();
  const dayMs  = 86_400_000;
  const prevEnd   = new Date(start.getTime() - dayMs);
  const prevStart = new Date(prevEnd.getTime() - durMs);
  const toLocal = (d) => {
    const yy = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    const dy = String(d.getDate()).padStart(2, "0");
    return `${yy}-${mo}-${dy}`;
  };
  return { startDate: toLocal(prevStart), endDate: toLocal(prevEnd) };
}

// ── Helpers ────────────────────────────────────────────────────────
function fmt(n, dec = 0) {
  const num = Number(n);
  if (n == null || isNaN(num)) return "—";
  return num.toFixed(dec);
}

function fmtDate(val) {
  if (!val) return "";
  const s = String(val).slice(0, 10);
  // new Date("2026-04-16") parses as UTC midnight → shifts back a day
  // in negative-offset timezones. Slashes force local-time parsing.
  const d = new Date(s.replace(/-/g, "/"));
  if (isNaN(d)) return val;
  return d.toLocaleDateString("en-US", {
    month: "2-digit", day: "2-digit", year: "2-digit",
  });
}

function safeNum(v) {
  const n = Number(v);
  return isNaN(n) ? 0 : n;
}

// Build a delta object from current/prev numeric values.
// `fmtVal` formats the prev value for display ("vs <prev>" suffix).
// Default = comma-formatted integer; pass a custom fn for percentages etc.
function buildDelta(cur, prev, { goodWhenUp = true, fmtVal } = {}) {
  const c = Number(cur);
  const p = Number(prev);
  if (!isFinite(c) || !isFinite(p) || p === 0) return null;
  const pct      = ((c - p) / p) * 100;
  const up       = pct > 0;
  const prevText = fmtVal ? fmtVal(p) : p.toLocaleString();
  return {
    up,
    good: goodWhenUp ? up : !up,
    text: `${up ? "+" : ""}${pct.toFixed(1)}% vs ${prevText}`,
  };
}

// Aggregate compliance rows (works for both summary and per-rule slices).
function aggregateCompliance(rows) {
  const total   = (rows || []).reduce((a,b) => a + safeNum(b.total_records),  0);
  const inComp  = (rows || []).reduce((a,b) => a + safeNum(b.in_compliance),  0);
  const outComp = (rows || []).reduce((a,b) => a + safeNum(b.out_compliance), 0);
  return {
    total, inComp, outComp,
    inPct:  total > 0 ? inComp  / total * 100 : null,
    outPct: total > 0 ? outComp / total * 100 : null,
  };
}

// ── Sub-components ─────────────────────────────────────────────────
function KpiCard({ label, value, color = BLUE, delta }) {
  return (
    <Box sx={{
      background: "var(--mui-palette-background-default, #f7f7f7)",
      borderRadius: 2, p: "10px 14px", minWidth: 0,
    }}>
      <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>{label}</Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 500, color, lineHeight: 1.2 }}>{value}</Typography>
      {delta && (
        <Typography sx={{ fontSize: 11, color: delta.good ? "#3B6D11" : "#A32D2D", mt: 0.5 }}>
          {delta.up ? "▲" : "▼"} {delta.text}
        </Typography>
      )}
    </Box>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography sx={{
      fontSize: 11, fontWeight: 500, color: "text.secondary",
      textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, mt: 2,
    }}>
      {children}
    </Typography>
  );
}

function ChartCard({ title, children, height = 200 }) {
  return (
    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, p: "12px 14px" }}>
      {title && (
        <Typography sx={{
          fontSize: 11, fontWeight: 500, color: "text.secondary",
          textTransform: "uppercase", letterSpacing: "0.05em", mb: 1,
        }}>
          {title}
        </Typography>
      )}
      <Box sx={{ width: "100%", height }}>{children}</Box>
    </Box>
  );
}

function PillGroup({ label, options, value, onChange, groups }) {
  let groupBoundaries = new Set();
  if (groups) {
    let idx = 0;
    groups.forEach((size, gi) => {
      if (gi < groups.length - 1) { idx += size; groupBoundaries.add(idx); }
    });
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography sx={{
        fontSize: 11, color: "text.secondary",
        textTransform: "uppercase", letterSpacing: "0.05em",
      }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
        {options.map((opt, i) => {
          const active = value === opt;
          return (
            <React.Fragment key={opt}>
              {groupBoundaries.has(i) && (
                <Box sx={{ width: "1px", height: 18, bgcolor: "divider", mx: 0.5, flexShrink: 0 }} />
              )}
              <Box onClick={() => onChange(opt)} sx={{
                px: "10px", py: "3px", fontSize: 12, borderRadius: "20px",
                cursor: "pointer", userSelect: "none",
                border: "0.5px solid",
                borderColor: active ? BLUE : "divider",
                background:  active ? BLUE : "transparent",
                color:       active ? "#fff" : "text.secondary",
                fontWeight:  active ? 500 : 400,
                transition:  "all .15s",
                "&:hover": { borderColor: BLUE },
              }}>
                {opt}
              </Box>
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
}

// ── Semi-circle gauge ──────────────────────────────────────────────
function GaugeCard({ label, value, subLabel, subValue, subColor = AMBER, pct = 0, color = BLUE }) {
  const clamp = Math.min(100, Math.max(0, safeNum(pct)));
  const r     = 52;
  const cx    = 70;
  const cy    = 66;
  const circ  = Math.PI * r;
  const dash  = (clamp / 100) * circ;
  return (
    <Box sx={{
      border: "0.5px solid", borderColor: "divider",
      borderRadius: 2, p: "12px 14px",
      display: "flex", flexDirection: "column", alignItems: "center",
    }}>
      <Typography sx={{
        fontSize: 11, color: "text.secondary", textTransform: "uppercase",
        letterSpacing: "0.05em", mb: 0.5, alignSelf: "flex-start",
      }}>
        {label}
      </Typography>
      <svg width={140} height={76} style={{ overflow: "visible" }}>
        <path d={`M ${cx-r} ${cy} A ${r} ${r} 0 0 1 ${cx+r} ${cy}`}
          fill="none" stroke="#e8e8e8" strokeWidth={12} strokeLinecap="round" />
        <path d={`M ${cx-r} ${cy} A ${r} ${r} 0 0 1 ${cx+r} ${cy}`}
          fill="none" stroke={color} strokeWidth={12} strokeLinecap="round"
          strokeDasharray={`${dash} ${circ}`} />
        <text x={cx} y={cy - 6} textAnchor="middle"
          style={{ fontSize: 20, fontWeight: 500, fill: color }}>
          {value}
        </text>
      </svg>
      {subLabel && (
        <>
          <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.5 }}>
            {subLabel}
          </Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 500, color: subColor, mt: 0.5 }}>
            {subValue}
          </Typography>
        </>
      )}
    </Box>
  );
}

// ── Rule section ───────────────────────────────────────────────────
function RuleSection({ groupName, rules, prevRules = [] }) {
  const cur  = aggregateCompliance(rules);
  const prev = aggregateCompliance(prevRules);

  // KPI deltas — total/inComp = good when up, outPct = bad when up, inPct = good when up.
  // Percentage deltas format the prev value with "%" suffix.
  const pctFmt = v => `${fmt(v, 2)}%`;
  const totalDelta  = buildDelta(cur.total,  prev.total,  { goodWhenUp: true  });
  const inCompDelta = buildDelta(cur.inComp, prev.inComp, { goodWhenUp: true  });
  const outPctDelta = buildDelta(cur.outPct, prev.outPct, { goodWhenUp: false, fmtVal: pctFmt });
  const inPctDelta  = buildDelta(cur.inPct,  prev.inPct,  { goodWhenUp: true,  fmtVal: pctFmt });

  const barData = rules.map(r => ({
    name:   r.rule_description,
    pct:    safeNum(r.in_compliance_pct),
    inComp: safeNum(r.in_compliance),
    total:  safeNum(r.total_records),
  })).sort((a,b) => b.pct - a.pct);

  const axisStyle = { fontSize: 10, fill: "#888" };

  return (
    <Box sx={{ mb: 2 }}>
      <SectionTitle>{groupName}</SectionTitle>

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))",
        gap: 1, mb: 1.5 }}>
        <KpiCard label="Total records"       value={cur.total}                                     color={BLUE} delta={totalDelta}  />
        <KpiCard label="In compliance"       value={cur.inComp}                                    color={BLUE} delta={inCompDelta} />
        <KpiCard label="Out of compliance %" value={cur.outPct == null ? "—" : `${fmt(cur.outPct, 2)}%`} color={PINK} delta={outPctDelta} />
        <KpiCard label="In compliance %"     value={cur.inPct  == null ? "—" : `${fmt(cur.inPct,  2)}%`} color={BLUE} delta={inPctDelta}  />
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 220px", gap: 1.5 }}>
        <ChartCard title="Compliance by rule" height={Math.max(160, barData.length * 36 + 40)}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={barData} layout="vertical"
              margin={{ top: 4, right: 60, bottom: 4, left: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.04)" horizontal={false} />
              <XAxis type="number" tick={axisStyle} domain={[0, 100]}
                tickFormatter={v => `${v}%`} />
              <YAxis type="category" dataKey="name"
                tick={{ fontSize: 10, fill: "#888" }} width={180} />
              <Tooltip
                contentStyle={{ fontSize: 12 }}
                formatter={(v, n, p) => [
                  `${fmt(v, 2)}% (${p.payload.inComp} / ${p.payload.total})`,
                  "In compliance"
                ]}
              />
              <Bar dataKey="pct" name="In compliance %" radius={[0,3,3,0]}>
                {barData.map((entry, i) => (
                  <Cell key={i} fill={entry.pct >= 70 ? BLUE : entry.pct >= 40 ? AMBER : PINK} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <GaugeCard
          label="In compliance % MTD"
          value={cur.inPct == null ? "—" : `${fmt(cur.inPct, 2)}%`}
          pct={safeNum(cur.inPct)}
          color={BLUE}
          subLabel="Total records"
          subValue={cur.total.toLocaleString()}
          subColor={AMBER}
        />
      </Box>
    </Box>
  );
}

// ── Main Dashboard ─────────────────────────────────────────────────
export default function ComplianceDashboard({ channel }) {
  const [gran,        setGran]        = useState("Day");
  const [rangePreset, setRangePreset] = useState("30D");
  const [campaign,    setCampaign]    = useState("All");
  const [campaigns,   setCampaigns]   = useState(["All"]);

  const [summary,     setSummary]     = useState([]);
  const [byRule,      setByRule]      = useState([]);
  const [mtd,         setMtd]         = useState({});
  // Prev-period datasets — drive KPI deltas only.
  const [prevSummary, setPrevSummary] = useState([]);
  const [prevByRule,  setPrevByRule]  = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState("");

  useEffect(() => {
    getCampaigns()
      .then(data => setCampaigns(["All", ...(data || []).map(c => c.name)]))
      .catch(() => {});
  }, []);

  const params = useMemo(() => ({
    ...getDateRange(rangePreset),
    campaign:    campaign === "All" ? undefined : campaign,
    granularity: gran.toLowerCase(),
    channel,
  }), [rangePreset, campaign, gran, channel]);

  const prevParams = useMemo(() => ({
    ...getPrevDateRange(params.startDate, params.endDate),
    campaign:    campaign === "All" ? undefined : campaign,
    granularity: gran.toLowerCase(),
    channel,
  }), [params.startDate, params.endDate, campaign, gran, channel]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [s, r, m, ps, pr] = await Promise.all([
        getSummary(params),
        getByRule(params),
        getMtd({ campaign: params.campaign, channel }),
        // Prev period: failures non-fatal — KPIs just won't show deltas.
        // MTD is intentionally not refetched — it's already month-to-date.
        getSummary(prevParams).catch(() => []),
        getByRule(prevParams).catch(() => []),
      ]);
      setSummary(s     || []);
      setByRule(r      || []);
      setMtd(m         || {});
      setPrevSummary(ps || []);
      setPrevByRule(pr  || []);
    } catch (err) {
      console.error("Compliance load error:", err?.response?.data, err?.message);
      setError(`Failed to load compliance data: ${err?.response?.data?.error || err?.message || "unknown"}`);
    } finally {
      setLoading(false);
    }
  }, [params, prevParams, channel]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Overall KPIs (with prev-period deltas) ─────────────────────
  const overallKpis = useMemo(() => {
    const cur  = aggregateCompliance(summary);
    const prev = aggregateCompliance(prevSummary);
    const pctFmt = v => `${fmt(v, 2)}%`;
    return {
      ...cur,
      totalDelta:  buildDelta(cur.total,  prev.total,  { goodWhenUp: true  }),
      inCompDelta: buildDelta(cur.inComp, prev.inComp, { goodWhenUp: true  }),
      outPctDelta: buildDelta(cur.outPct, prev.outPct, { goodWhenUp: false, fmtVal: pctFmt }),
      inPctDelta:  buildDelta(cur.inPct,  prev.inPct,  { goodWhenUp: true,  fmtVal: pctFmt }),
    };
  }, [summary, prevSummary]);

  // ── Chart data ─────────────────────────────────────────────────
  const chartData = useMemo(() =>
    summary.map(r => ({
      date:  fmtDate(r.summary_date),
      inPct: safeNum(r.in_compliance_pct),
      total: safeNum(r.total_records),
    }))
  , [summary]);

  // ── Group rules by groupname, paired with their prev-period rows ─
  const ruleGroups = useMemo(() => {
    const curMap = {};
    byRule.forEach(r => {
      const key = r.group_name || r.rule_id || "Other";
      (curMap[key] = curMap[key] || []).push(r);
    });
    const prevMap = {};
    prevByRule.forEach(r => {
      const key = r.group_name || r.rule_id || "Other";
      (prevMap[key] = prevMap[key] || []).push(r);
    });
    return Object.entries(curMap)
      .sort((a,b) => a[0].localeCompare(b[0]))
      .map(([key, rules]) => ({ groupName: key, rules, prevRules: prevMap[key] || [] }));
  }, [byRule, prevByRule]);

  const axisStyle    = { fontSize: 10, fill: "#888" };
  const tooltipStyle = { fontSize: 12 };

  return (
    <Box sx={{ p: 2 }}>

      {/* ── Slicers ── */}
      <Box sx={{
        display: "flex", flexWrap: "wrap", gap: 2,
        alignItems: "flex-end",
        pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
      }}>
        <PillGroup label="Granularity" options={GRAN_OPTIONS} value={gran} onChange={setGran} />
        <PillGroup
          label="Date range"
          options={RANGE_OPTIONS}
          value={rangePreset}
          onChange={setRangePreset}
          groups={[4, 2, 4]}
        />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignSelf: "flex-end" }}>
          <Typography sx={{
            fontSize: 11, color: "text.secondary",
            textTransform: "uppercase", letterSpacing: "0.05em",
          }}>
            Campaign
          </Typography>
          <Select
            value={campaign}
            onChange={e => setCampaign(e.target.value)}
            size="small" variant="outlined"
            sx={{
              fontSize: 12, borderRadius: "20px", minWidth: 120, height: 28,
              ".MuiOutlinedInput-notchedOutline": { borderColor: "divider", borderWidth: "0.5px" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BLUE },
              ".MuiSelect-select": { py: "3px", px: "10px", fontSize: 12, color: "text.secondary" },
              ".MuiSvgIcon-root": { fontSize: 16, color: "text.secondary" },
            }}
          >
            {campaigns.map(c => (
              <MenuItem key={c} value={c} sx={{ fontSize: 12 }}>{c}</MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 2 }}>
          <CircularProgress size={20} /> Loading compliance data…
        </Box>
      )}

      {!loading && (
        <>
          {/* ── OVERALL COMPLIANCE ────────────────────────────── */}
          <SectionTitle>{channel === "emails" ? "Emails" : "Texts"} compliance</SectionTitle>

          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))",
            gap: 1, mb: 1.5 }}>
            <KpiCard label="Total records"       value={overallKpis.total.toLocaleString()}                                  color={BLUE} delta={overallKpis.totalDelta}  />
            <KpiCard label="In compliance"       value={overallKpis.inComp.toLocaleString()}                                 color={BLUE} delta={overallKpis.inCompDelta} />
            <KpiCard label="Out of compliance %" value={overallKpis.outPct == null ? "—" : `${fmt(overallKpis.outPct, 2)}%`} color={PINK} delta={overallKpis.outPctDelta} />
            <KpiCard label="In compliance %"     value={overallKpis.inPct  == null ? "—" : `${fmt(overallKpis.inPct,  2)}%`} color={BLUE} delta={overallKpis.inPctDelta}  />
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 220px", gap: 1.5, mb: 2 }}>
            <ChartCard title="In compliance % over time" height={220}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="compGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={BLUE} stopOpacity={0.15} />
                      <stop offset="95%" stopColor={BLUE} stopOpacity={0}    />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="date" tick={axisStyle} />
                  <YAxis tick={axisStyle} domain={[0, 100]}
                    tickFormatter={v => `${v}%`} />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={(v, n, p) => [
                      `${fmt(v, 2)}% (${p.payload.total.toLocaleString()} records)`,
                      "In compliance"
                    ]}
                  />
                  <Area type="stepAfter" dataKey="inPct" name="In compliance %"
                    stroke={BLUE} fill="url(#compGrad)" strokeWidth={2} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>

            <GaugeCard
              label="In compliance % MTD"
              value={`${fmt(safeNum(mtd.in_compliance_pct), 2)}%`}
              pct={safeNum(mtd.in_compliance_pct)}
              color={BLUE}
              subLabel="Total records MTD"
              subValue={safeNum(mtd.total_records).toLocaleString()}
              subColor={AMBER}
            />
          </Box>

          {/* ── PER-GROUP RULE SECTIONS ────────────────────────── */}
          {ruleGroups.map(g => (
            <RuleSection
              key={g.groupName}
              groupName={g.groupName}
              rules={g.rules}
              prevRules={g.prevRules}
            />
          ))}
        </>
      )}
    </Box>
  );
}